import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  ReactNode,
} from 'react';
import { ApolloError, useApolloClient, useQuery, useReactiveVar, useSubscription } from '@apollo/client';
import {
  currentCountryVar,
  currentShopIdVar,
  useCurrentToken,
} from '_graphql/cache/auth';
import { SUPPLIER_INBOX } from '_graphql/queries/inbox';
import { SUPPLIER_INBOX_UPDATED } from '_graphql/subscriptions/inbox';
import {
  SupplierInbox,
  SupplierInbox_supplierInbox,
  SupplierInboxVariables,
} from '_graphql/queries/__generated__/SupplierInbox';
import { SupplierInboxUpdated } from '_graphql/subscriptions/__generated__/SupplierInboxUpdated';
import { onGraphqlWsConnected, restartGraphqlWs } from '_graphql/ws';
import { OPEN_INBOX_VARIABLES, applyInboxEvent, nextInboxCount } from './applyInboxEvent';

type InboxContextValue = {
  items: SupplierInbox_supplierInbox[];
  count: number;
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
};

const InboxContext = createContext<InboxContextValue | null>(null);

export const useSupplierInboxOptional = () => useContext(InboxContext);

export const useSupplierInbox = () => {
  const value = useContext(InboxContext);
  if (!value) {
    throw new Error('useSupplierInbox must be used inside InboxProvider');
  }
  return value;
};

export const InboxProvider = ({ children }: { children: ReactNode }) => {
  const token = useCurrentToken();
  const country = useReactiveVar(currentCountryVar);
  const shopId = useReactiveVar(currentShopIdVar);
  const ready = Boolean(token);
  const prevAuth = useRef({ country, token, shopId });
  const client = useApolloClient();

  const { data, loading, error, refetch } = useQuery<
    SupplierInbox,
    SupplierInboxVariables
  >(SUPPLIER_INBOX, {
    skip: !ready,
    fetchPolicy: 'cache-and-network',
    variables: OPEN_INBOX_VARIABLES,
  });

  useSubscription<SupplierInboxUpdated>(SUPPLIER_INBOX_UPDATED, {
    skip: !ready,
    onData: ({ data: result }) => {
      const event = result.data?.supplierInboxUpdated;
      if (!event) return;

      client.cache.updateQuery<SupplierInbox, SupplierInboxVariables>(
        { query: SUPPLIER_INBOX, variables: OPEN_INBOX_VARIABLES },
        (prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            supplierInbox: applyInboxEvent(prev.supplierInbox, event),
            supplierInboxCount: nextInboxCount(
              prev.supplierInbox,
              prev.supplierInboxCount,
              event,
            ),
          };
        },
      );
    },
  });

  useEffect(() => {
    const countryChanged = prevAuth.current.country !== country;
    const tokenChanged = prevAuth.current.token !== token;
    const shopChanged = prevAuth.current.shopId !== shopId;
    prevAuth.current = { country, token, shopId };
    if (!countryChanged && !tokenChanged && !shopChanged) return;
    if (ready) {
      restartGraphqlWs();
    }
  }, [country, token, shopId, ready]);

  useEffect(() => {
    return onGraphqlWsConnected(() => {
      if (ready) {
        refetch();
      }
    });
  }, [ready, refetch]);

  const refetchOpen = useCallback(() => {
    if (ready) refetch();
  }, [ready, refetch]);

  const value = useMemo<InboxContextValue>(
    () => ({
      items: data?.supplierInbox ?? [],
      count: data?.supplierInboxCount ?? 0,
      loading: ready && loading && !data,
      error,
      refetch: refetchOpen,
    }),
    [ready, data, loading, error, refetchOpen],
  );

  return (
    <InboxContext.Provider value={value}>{children}</InboxContext.Provider>
  );
};
